import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  Lock,
  ShieldCheck,
  Activity,
  Save,
  Loader2
} from 'lucide-react';
import styles from './UserDrawer.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomSelect from '../../components/Common/Select/CustomSelect';

const UserDrawer = ({ isOpen, onClose, onSave, user = null }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    name: '',
    email: '',
    mobile: '',
    password: '',
    role: '',
    status: 1,
  });

  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchUserDetail = async () => {
      if (user && isOpen) {
        setIsFetching(true);
        try {
          // Pre-populate with existing data from props while fetching
          setFormData({
            employee_id: user.employee_id || '',
            name: user.name || '',
            email: user.email || '',
            mobile: user.mobile || '',
            password: '',
            role: user.roles?.[0]?.name || '',
            status: user.status ?? 1,
          });

          const response = await api.get(`/users/${user.id}`);
          if (response.data.success) {
            const data = response.data.data;
            setFormData({
              employee_id: data.employee_id || '',
              name: data.name || '',
              email: data.email || '',
              mobile: data.mobile || '',
              password: '',
              role: data.roles?.[0]?.name || '',
              status: data.status !== undefined ? data.status : 1,
            });
          }
        } catch (error) {
          console.error('Error fetching user details:', error);
          toast.error('Failed to fetch latest user details');
        } finally {
          setIsFetching(false);
        }
      } else if (!user) {
        setFormData({
          employee_id: '',
          name: '',
          email: '',
          mobile: '',
          password: '',
          role: '',
          status: 1,
        });
        setIsFetching(false);
      }
    };

    if (isOpen) {
      fetchRoles();
      fetchEmployees();
      fetchUserDetail();
    }
  }, [isOpen, user]);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const url = user && user.employee_id
        ? `/employees-dropdown?has_no_user=1&include_employee_id=${user.employee_id}`
        : '/employees-dropdown?has_no_user=1';
      const response = await api.get(url);
      if (response.data.success) {
        setEmployees(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleEmployeeSelect = (selectedOption) => {
    if (selectedOption) {
      const employee = employees.find(emp => emp.id === selectedOption.value);
      setFormData(prev => ({
        ...prev,
        employee_id: selectedOption.value,
        name: employee ? employee.employee_name : selectedOption.label,
        email: employee ? (employee.email || prev.email) : prev.email,
        mobile: employee ? (employee.phone_number || prev.mobile) : prev.mobile
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        employee_id: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({ ...prev, status }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      role: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      const payload = { ...formData };

      // If editing and password is empty, remove it from payload
      if (user && !payload.password) {
        delete payload.password;
      }

      if (user) {
        response = await api.put(`/users/${user.id}`, payload);
      } else {
        response = await api.post('/users', payload);
      }

      if (response.data.success) {
        toast.success(user ? 'User updated successfully' : 'User created successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving user:', error);
      const message = error.response?.data?.message || 'Failed to save user';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{user ? 'Edit User' : 'Add User'}</h2>
            <p>Fill in the system user account details</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {isFetching ? (
              <div className={styles['loading-container']}>
                <Loader2 size={40} className={styles['spinner']} />
                <span>Fetching user details...</span>
              </div>
            ) : (
              <>
                {/* Select Employee */}
                <div className={styles['form-group']}>
                  <label><User size={16} /> Select Existing Employee <span className={styles['required-star']}>*</span> </label>
                  <CustomSelect
                    name="employee_select"
                    value={formData.employee_id ? {
                      value: formData.employee_id,
                      label: employees.find(emp => emp.id === formData.employee_id)?.employee_name || 'Selected Employee'
                    } : null}
                    onChange={handleEmployeeSelect}
                    options={employees.map(emp => ({
                      value: emp.id,
                      label: emp.employee_name
                    }))}
                    placeholder="Search and select employee..."
                    isClearable
                  />
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Selecting an employee will pre-fill the name, email, and mobile fields.
                  </p>
                </div>

                <div className={styles['dropdown-divider']}></div>

                {/* Full Name */}
                <div className={styles['form-group']}>
                  <label><User size={16} /> Full Name <span className={styles['required-star']}>*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    required
                    readOnly
                    minLength={2}
                    maxLength={200}

                  />
                </div>

                {/* Email */}
                <div className={styles['form-group']}>
                  <label><Mail size={16} /> Email Address <span className={styles['required-star']}>*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                    readOnly
                    maxLength={200}
                  />
                </div>

                {/* Mobile */}
                <div className={styles['form-group']}>
                  <label><Phone size={16} /> Mobile Number <span className={styles['required-star']}>*</span></label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    required
                    readOnly
                    pattern="[0-9]{10,12}"
                    title="Mobile number should be 10 to 12 digits"
                  />
                </div>

                {/* Password */}
                <div className={styles['form-group']}>
                  <label><Lock size={16} /> Password {user ? '(Leave blank to keep current)' : <span className={styles['required-star']}>*</span>}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    required={!user}
                    minLength={8}
                  />
                </div>

                {/* Role */}
                <div className={styles['form-group']}>
                  <label><ShieldCheck size={16} /> System Role <span className={styles['required-star']}>*</span></label>
                  <CustomSelect
                    name="role"
                    value={formData.role ? { value: formData.role, label: formData.role.charAt(0).toUpperCase() + formData.role.slice(1) } : null}
                    onChange={handleSelectChange}
                    options={roles.map(role => ({
                      value: role.name,
                      label: role.name.charAt(0).toUpperCase() + role.name.slice(1)
                    }))}
                    placeholder="Select Role"
                    required
                  />
                </div>

                {/* Status */}
                <div className={styles['form-group']}>
                  <label><Activity size={16} /> Account Status</label>
                  <div className={styles['status-toggle-wrapper']}>
                    <button
                      type="button"
                      className={`${styles['status-btn']} ${formData.status === 1 ? styles['active'] : ''}`}
                      onClick={() => handleStatusChange(1)}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      className={`${styles['status-btn']} ${formData.status === 0 ? styles['inactive'] : ''}`}
                      onClick={() => handleStatusChange(0)}
                    >
                      Inactive
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{user ? 'Update User' : 'Save User'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDrawer;
